import { useEffect } from 'react';

/**
 * Prevents browser page zoom (Ctrl+Scroll / ⌘+Scroll) while `enabled` is
 * true, and manually forwards the scroll delta to the nearest scrollable
 * ancestor so the underlying list still scrolls normally.
 *
 * Attach this to any picker overlay component so that holding Ctrl to
 * multi-select does not accidentally zoom the page.
 */
export const useCtrlWheelZoomPrevention = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled) return;

    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();

      // Convert deltaY to pixels regardless of deltaMode.
      const deltaY =
        e.deltaMode === 1
          ? e.deltaY * 40 // line mode
          : e.deltaMode === 2
            ? e.deltaY * window.innerHeight // page mode
            : e.deltaY; // pixel mode (most common)

      // Walk up from the event target to find the nearest scrollable
      // container and scroll it manually.
      let el = e.target as HTMLElement | null;
      while (el) {
        const { overflowY } = window.getComputedStyle(el);
        if (overflowY === 'auto' || overflowY === 'scroll') {
          el.scrollTop += deltaY;
          return;
        }
        el = el.parentElement;
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, [enabled]);
};
